import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import { LockRounded } from '@mui/icons-material';

import useUserInfo from '@src/hooks/useTtsUser';
import { useSelector } from 'react-redux';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { useTranslation } from 'react-i18next';
import Tooltip from '@src/components/Tooltip';
import { getPackageName, getSMSPackageName } from '@src/services/package';
import i18n from '@src/languages';
import { delimitNumber } from '@src/utils/number';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import {
  FREE_PACKAGE_CODES,
  PACKAGE_LEVEL,
  PACKAGE_STUDIO_VERSION,
} from '@src/constants/package';
import { StyledDetailPackageInfo } from './index.style';

const DetailPackageInfo = ({
  useLockCycle,
  useLockTopUp,
  useLockCustom,
  purchasedTopUpCredits,
  isSMSUser,
  getPackageExpired,
  isExpiredPackage,
  remainingCycleCredits,
  remainingTopUpCredits,
  remainingCustomCredits,
  remainingCharacters = 0,
  lockCharacters = 0,
  bonusCharacters = 0,
}) => {
  const { language } = i18n;
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();
  const { usingPackage, lastExpiredOrder } = useSelector((state) => state.user);
  const { getFeatureValue } = useFeatureFlags();
  const isLockedCycle = ttsUser?.studio?.cycle?.isLocked;
  const isLockedTopUp = ttsUser?.studio?.topUp?.isLocked;
  const isLockedCustom = ttsUser?.studio?.custom?.isLocked;

  const migrateOldPkgToV3 = getFeatureValue(
    FEATURE_KEYS.MIGRATE_OLD_PKG_TO_V3,
    { userId: user.id },
  );

  const totalOldRemainingCharacters =
    remainingCharacters + bonusCharacters || 0;

  const isFreePackage = FREE_PACKAGE_CODES.includes(user?.packageCode);

  const userUseOneTimePackage = useMemo(() => {
    const packageVersion = usingPackage?.version || PACKAGE_STUDIO_VERSION.V1;
    return [
      PACKAGE_STUDIO_VERSION.V1,
      PACKAGE_STUDIO_VERSION.V2_1,
      PACKAGE_STUDIO_VERSION.V2,
    ].includes(packageVersion);
  }, [usingPackage]);

  const userUseCustomPackage = useMemo(() => {
    const packageLevel = usingPackage?.level || '';
    return packageLevel === PACKAGE_LEVEL.CUSTOM;
  }, [usingPackage]);

  const getTextPackageExpired = () => {
    let expiryDateText = getPackageExpired();
    if (isExpiredPackage && userUseOneTimePackage && lockCharacters > 0) {
      expiryDateText = `${expiryDateText} (${t('expired').toLowerCase()})`;
    }

    return expiryDateText;
  };

  let packageName =
    !isSMSUser && usingPackage.id
      ? getPackageName(
          usingPackage.name,
          usingPackage.code,
          language,
          t,
          true,
          migrateOldPkgToV3,
        )
      : getPackageName(
          lastExpiredOrder?.package?.name,
          user?.packageCode,
          language,
          t,
          true,
          migrateOldPkgToV3,
        );

  if (usingPackage.id && isSMSUser) {
    packageName = getSMSPackageName({
      name: usingPackage?.name,
      language,
    });
  }

  const renderTitleIconLockCredits = () => {
    if (purchasedTopUpCredits && !useLockTopUp) {
      return (
        <Typography
          component="span"
          dangerouslySetInnerHTML={{
            __html: t('contentTooltipLockCycleHasCharacters', {
              characters: delimitNumber(purchasedTopUpCredits, language),
            }),
          }}
        />
      );
    }

    return t('contentTooltipLockCredits');
  };

  const renderIconTooltipLockCredits = () => {
    const titleTooltip = renderTitleIconLockCredits();
    if (
      (isLockedCycle && useLockCycle && remainingCycleCredits) ||
      (isLockedCustom && useLockCustom && remainingCustomCredits)
    )
      return (
        <Tooltip
          arrow
          className="tootlip"
          title={titleTooltip}
          placement="left"
        >
          <LockRoundedIcon className="icon-lock-cycle" />
        </Tooltip>
      );

    if (isLockedTopUp && useLockTopUp && remainingTopUpCredits)
      return (
        <Tooltip
          arrow
          className="tootlip"
          title={titleTooltip}
          placement="left"
        >
          <LockRoundedIcon className="icon-lock-cycle" />
        </Tooltip>
      );

    return <></>;
  };

  return (
    <StyledDetailPackageInfo>
      <Box className="package-info-container">
        <Box className="package-info">
          <Typography className="package-name">
            {`${t('pack')} ${packageName}`}
          </Typography>
          <Typography
            className={`package-expiry-date ${
              isExpiredPackage && 'danger-text'
            }`}
          >
            <span className="vertical-bar">|</span> {getTextPackageExpired()}
          </Typography>
        </Box>

        {isFreePackage && !userUseOneTimePackage && (
          <Box className="free-characters">
            <Typography className="remain-free-characters">
              {user?.bonusCharacters?.toLocaleString(language) || 0}{' '}
              {t('characters')}
            </Typography>
            <Typography
              className="free-characters-note"
              component="span"
              sx={{ fontSize: '12px' }}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  t('introductoryPeriodDays', {
                    introductoryPeriodDays:
                      usingPackage?.freemiumConfig?.introductoryPeriodDays,
                    introductoryDailyCredits:
                      usingPackage?.freemiumConfig?.introductoryDailyCredits?.toLocaleString(
                        language,
                      ),
                    regularDailyCredits:
                      usingPackage?.freemiumConfig?.regularDailyCredits?.toLocaleString(
                        language,
                      ),
                  }),
                ),
              }}
            />
          </Box>
        )}

        {/* check cycle packages */}
        {!isFreePackage && !userUseOneTimePackage && !userUseCustomPackage && (
          <Box className="cycle-characters">
            <Typography className="remain-cycle-characters">
              {isExpiredPackage
                ? 0
                : `${delimitNumber(
                    remainingCycleCredits +
                      remainingTopUpCredits +
                      bonusCharacters,
                    language,
                  )} ${t('characters').toLocaleLowerCase()}`}
            </Typography>
            {renderIconTooltipLockCredits()}
            <Typography className="cycle-characters-note">
              {`(${t('remainCycleCharacters')})`}
            </Typography>
          </Box>
        )}

        {/* if user use custom package */}
        {userUseCustomPackage && (
          <Box className="custom-characters">
            <Typography className="remain-custom-characters">
              {`${delimitNumber(remainingCustomCredits, language)} ${t(
                'characters',
              ).toLocaleLowerCase()}`}
            </Typography>
            {renderIconTooltipLockCredits()}
            {remainingCharacters > 0 && (
              <Typography className="old-characters-note">
                {`(${t('remainCurrentPlanCharacters')})`}
              </Typography>
            )}
          </Box>
        )}

        {/* if user have V3 package and remainingCharacter */}
        {/* if user have old package and valid remainingChracters */}
        {((!userUseOneTimePackage && remainingCharacters > 0) ||
          (userUseOneTimePackage && !isExpiredPackage)) && (
          <Box className="old-characters">
            <Typography className="remain-old-characters">
              {`${delimitNumber(totalOldRemainingCharacters, language)} ${t(
                'characters',
              ).toLocaleLowerCase()}`}
            </Typography>
            <Typography className="old-characters-note">
              {`(${t('remainOldCharacters')})`}
            </Typography>
          </Box>
        )}

        {/* if user have old package and expired */}
        {userUseOneTimePackage && isExpiredPackage && (
          <>
            <Box className="old-characters">
              <Typography className="remain-old-characters">
                {`${delimitNumber(lockCharacters, language)} ${t(
                  'characters',
                ).toLocaleLowerCase()}`}
              </Typography>
              <Typography className="old-characters-note">
                {lockCharacters > 0 && (
                  <LockRounded sx={{ fontSize: 16 }} className="lock-icon" />
                )}
              </Typography>
            </Box>

            {lockCharacters > 0 && (
              <Typography className="preserver-date">{`${t(
                'preservedCharactersDateV2',
              )}: ${moment(user?.preservationExpiryDate).format(
                'DD/MM/YYYY - HH:mm',
              )}`}</Typography>
            )}
          </>
        )}
      </Box>
    </StyledDetailPackageInfo>
  );
};

export default DetailPackageInfo;
